import Promise from 'bluebird'
import jwtDecode from 'jwt-decode'
import Vue from 'vue'
import Sentry from './sentry/client'
import {CognitoUserPool} from 'amazon-cognito-identity-js'

const {CognitoIdentityCredentials} = AWS
const REGION = process.env.REGION
const USER_POOL_ID = process.env.USER_POOL_ID
const USER_POOL_CLIENT_ID = process.env.USER_POOL_CLIENT_ID
const IDENTITY_POOL_ID = process.env.IDENTITY_POOL_ID

AWS.config.region = REGION

const MANAGED_ATTRIBUTES = ['name', 'email', 'phone_number', 'custom:config']
const SERIALIZED = ['config']
const CUSTOM_PREFIX = 'custom:'

const POOL = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID
})


const cognitoUser = POOL.getCurrentUser()
const shared = new Vue({
  data(){
    const tmp = {
      loading: false,
      login: cognitoUser ? cognitoUser.username : null,
      id: '',
      groups: [],
      sessionToken: '',
      accessToken: '',
      email_verified: false,
      authenticated: false
    }

    MANAGED_ATTRIBUTES.forEach(key => {
      tmp[key.replace(CUSTOM_PREFIX, '')] = ''
    })

    return tmp
  },

  computed: {
    isAdmin(){
      return this.groups.indexOf('admins') !== -1
    }
  },

  methods: {
    logout(origin){
      install.user.logout()
      .finally(() => {
        this.resetAttributes()
        this.toLogin(origin)
      })
    },

    resetAttributes(){
      // reset all
      this.login = null
      this.id = ''
      this.authenticated = false
      this.groups = []
      this.sessionToken = ''
      this.accessToken = ''

      MANAGED_ATTRIBUTES.forEach(key => {
        this[key.replace(CUSTOM_PREFIX, '')] = ''
      })
    },

    toLogin(origin){
      if(origin === false) return window.location.reload()

      let loginUri = '/login'
      if(origin) loginUri += `#/?redirect_url=${encodeURIComponent(origin)}`
      window.location.replace(loginUri)
    },

    getCognitoUser(){
      return user.cognitoUser
    },

    showLoginDialog(){
      this.$dialog.confirm({
        message: 'Du musst angemeldet sein um dieses Feature zu nutzen.',
        confirmText: 'Zum Login',
        cancelText: 'Abbrechen',
        onConfirm: () => this.$user.toLogin(window.location.pathname)
      })
    }
  }
})





export default function install(Vue) {
  if (install.installed) return
  install.installed = true

  Object.defineProperty(Vue.prototype, '$user', {
    get(){
      return shared
    }
  })
}



const user = install.user = {
  promise: null,
  cognitoUser,
  shared,

  isAuthenticated(groups){
    if(!this.promise) return this.authenticate(groups)
    return this.promise
    .then(() => {
      return AWS.config.credentials.getPromise() // refreh the identity session (AWS credentials)
    })
    .then(() => {
      return this.getSession() // refresh the cognito user session
    })
  },

  getSession(){
    if(!this.cognitoUser) return Promise.reject(new Error('unauthenticated'))

    return new Promise((resolve, reject) => {
      this.cognitoUser.getSession((error, session) => {
        if(error) return reject(error)
        if(!session.isValid()) return reject(new Error('unauthenticated'))

        this.session = session

        resolve(session)
      })
    })
  },


  getUserAttributes(){
    return new Promise((resolve, reject) => {
      this.cognitoUser.getUserAttributes((error, attributes) => {
        if(error) return reject(error)
        resolve(this.normalize({Attributes: attributes}))
      })
    })
  },


  getSessionToken(session){
    return new Promise((resolve, reject) => {
      const identity = new CognitoIdentityCredentials({
        IdentityPoolId: IDENTITY_POOL_ID,
        Logins: {
          [`cognito-idp.${REGION}.amazonaws.com/${USER_POOL_ID}`]: session.idToken.jwtToken
        }
      })

      identity.refresh(error => {
        if(error) return reject(error)

        // authentication for aws sdk
        AWS.config.credentials = identity

        resolve(identity.sessionToken)
      })
    })
  },


  setToken(sessionToken){
    this.shared.sessionToken = sessionToken
    this.shared.accessToken = this.session.accessToken.jwtToken
  },

  setUserAttributes(attributes){
    MANAGED_ATTRIBUTES.forEach(key => {
      key = key.replace(CUSTOM_PREFIX, '')
      this.shared[key] = attributes[key]

      if(this.shared[key] && SERIALIZED.indexOf(key) !== -1){
        try{
          this.shared[key] = JSON.stringify(this.shared[key])
        }catch(e){
          console.log('FAILED to parse config', this.shared[key])
        }
      }
    })

    // delay change notifications
    setTimeout(() => {
      this.shared.id = AWS.config.credentials.data.IdentityId
      this.shared.authenticated = true
      this.shared.loading = false
    }, 10)
  },

  setUserLoading(session){
    this.shared.loading = true
    return session
  },


  authenticate(groups){
    this.promise = this.getSession()
    .bind(this)
    .then(this.setUserLoading)
    .then(this.getSessionToken)
    .then(this.setToken)
    .then(this.getUserAttributes)
    .then(this.setUserAttributes)
    .then(() => {
      Sentry.setUser(this.shared)
    })
    .then(() => {
      const decoded = jwtDecode(this.cognitoUser.signInUserSession.idToken.jwtToken)
      if(decoded) this.shared.groups = decoded['cognito:groups'] || []
      if(!decoded || (intersect(groups, decoded['cognito:groups']).length === 0 && groups.length > 0)) throw new Error('unauthenticated')
    })

    return this.promise
  },



  logout(){
    return new Promise((resolve, reject) => {
      this.cognitoUser.globalSignOut({
        onSuccess: resolve,
        onFailure: reject
      })
    })
  },




  normalize(user){
    const tmp = {
      login: user.Username,
      status: user.UserStatus,
      updated_at: user.UserLastModifiedDate,
      created_at: user.UserCreateDate,
      enabled: user.Enabled
    }

    MANAGED_ATTRIBUTES.forEach(key => {
      tmp[key.replace(CUSTOM_PREFIX, '')] = ''
    })
    const attrs = user.UserAttributes || user.Attributes || []

    attrs.forEach(attr => {
      if(attr.Name === 'sub') attr.Name = 'id'
      tmp[attr.Name.replace(CUSTOM_PREFIX, '')] = attr.Value
    })

    return tmp
  }
}



function intersect(a, b){
  if(!a || !b) return []
  let ai = 0
  let bi = 0
  const result = []

  while(ai < a.length && bi < b.length) {
    if (a[ai] < b[bi]){ ai++ } else if (a[ai] > b[bi]){
      bi++
    }else{
      result.push(a[ai])
      ai++
      bi++
    }
  }

  return result
}





export {
  MANAGED_ATTRIBUTES,
  CUSTOM_PREFIX,
  REGION,
  USER_POOL_ID,
  USER_POOL_CLIENT_ID,
  IDENTITY_POOL_ID,
  shared,
  user
}



// WEBPACK FOOTER //
// ./shared/user.js